// Page Transition System for The House of Chopras
// Handles animated transitions between routes with a terminal/glitch aesthetic

import { gsap } from 'gsap';
import { prefersReducedMotion, glitch } from './animations';
import { router, type NavigationEvent, type RouteChangeListener } from './router';
import { screenCrack } from './effects';

/**
 * Transition types available for page changes
 */
export type TransitionType =
  | 'fade'           // Simple fade out/in
  | 'glitch'         // Glitch effect with distortion
  | 'scanline'       // CRT scanline sweep
  | 'terminal'       // Terminal screen clear
  | 'pixelate'       // Pixel dissolve effect
  | 'static'         // TV static interruption
  | 'crack'          // Screen crack/shatter effect
  | 'typewriter';    // Typewriter cascade fill/clear

/**
 * Configuration for a page transition
 */
export interface TransitionConfig {
  /** Type of transition effect */
  type: TransitionType;
  /** Duration of the exit animation in seconds */
  exitDuration: number;
  /** Duration of the enter animation in seconds */
  enterDuration: number;
  /** Delay between exit complete and enter start */
  staggerDelay: number;
  /** Color for flash/overlay effects */
  overlayColor?: string;
}

/**
 * Route-specific transition overrides
 */
interface RouteTransitionMap {
  [key: string]: {
    from?: TransitionConfig;
    to?: TransitionConfig;
  };
}

// Default transition configuration
const DEFAULT_TRANSITION: TransitionConfig = {
  type: 'glitch',
  exitDuration: 0.35,
  enterDuration: 0.4,
  staggerDelay: 0.05,
  overlayColor: 'var(--red)',
};

// Reduced motion transition - instant/minimal
const REDUCED_MOTION_TRANSITION: TransitionConfig = {
  type: 'fade',
  exitDuration: 0.1,
  enterDuration: 0.15,
  staggerDelay: 0,
};

// Route-specific transitions for thematic consistency
const ROUTE_TRANSITIONS: RouteTransitionMap = {
  // Entry to House - typewriter cascade
  '->house': {
    to: {
      type: 'typewriter',
      exitDuration: 0.5,
      enterDuration: 0.4,
      staggerDelay: 0.08,
    },
  },
  // House to hidden areas - static/glitch for mystery
  'house->basement': {
    to: {
      type: 'static',
      exitDuration: 0.4,
      enterDuration: 0.4,
      staggerDelay: 0.15,
    },
  },
  'house->attic': {
    to: {
      type: 'static',
      exitDuration: 0.4,
      enterDuration: 0.4,
      staggerDelay: 0.15,
    },
  },
  'house->archive': {
    to: {
      type: 'static',
      exitDuration: 0.4,
      enterDuration: 0.4,
      staggerDelay: 0.15,
    },
  },
  // Guestbook uses terminal style
  '->guestbook': {
    to: {
      type: 'terminal',
      exitDuration: 0.3,
      enterDuration: 0.35,
      staggerDelay: 0.1,
    },
  },
  // Videos use scanline for CRT feel
  '->videos': {
    to: {
      type: 'scanline',
      exitDuration: 0.35,
      enterDuration: 0.35,
      staggerDelay: 0.08,
    },
  },
};

/**
 * Get the transition configuration for a route change
 */
function getTransitionConfig(from: string, to: string): TransitionConfig {
  if (prefersReducedMotion()) {
    return REDUCED_MOTION_TRANSITION;
  }

  // Check for specific route pair
  const specificKey = `${from}->${to}`;
  if (ROUTE_TRANSITIONS[specificKey]?.to) {
    return { ...DEFAULT_TRANSITION, ...ROUTE_TRANSITIONS[specificKey].to };
  }

  // Check for "any -> to" pattern
  const toKey = `->${to}`;
  if (ROUTE_TRANSITIONS[toKey]?.to) {
    return { ...DEFAULT_TRANSITION, ...ROUTE_TRANSITIONS[toKey].to };
  }

  // Check for "from -> any" pattern
  const fromKey = `${from}->`;
  if (ROUTE_TRANSITIONS[fromKey]?.from) {
    return { ...DEFAULT_TRANSITION, ...ROUTE_TRANSITIONS[fromKey].from };
  }

  return DEFAULT_TRANSITION;
}

/**
 * Create a transition overlay element
 */
function createOverlay(type: TransitionType): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = `transition-overlay transition-overlay--${type}`;
  overlay.setAttribute('aria-hidden', 'true');
  document.body.appendChild(overlay);
  return overlay;
}

/**
 * Remove the transition overlay
 */
function removeOverlay(overlay: HTMLElement): void {
  if (overlay.parentNode) {
    overlay.parentNode.removeChild(overlay);
  }
}

/**
 * Execute the fade transition
 */
async function fadeTransition(
  app: HTMLElement,
  config: TransitionConfig,
  renderNewPage: () => Promise<void>
): Promise<void> {
  // Fade out current content
  await gsap.to(app, {
    opacity: 0,
    duration: config.exitDuration,
    ease: 'power2.in',
  });

  // Render new page
  await renderNewPage();

  // Fade in new content
  gsap.set(app, { opacity: 0 });
  await gsap.to(app, {
    opacity: 1,
    duration: config.enterDuration,
    ease: 'power2.out',
  });
}

/**
 * Execute the glitch transition
 */
async function glitchTransition(
  app: HTMLElement,
  config: TransitionConfig,
  renderNewPage: () => Promise<void>
): Promise<void> {
  const overlay = createOverlay('glitch');

  // Apply glitch effect to current page
  const glitchTl = glitch(app, 7);
  await glitchTl;

  // Flash and distort
  gsap.set(overlay, { opacity: 0 });
  await gsap.to(overlay, {
    opacity: 1,
    duration: config.exitDuration * 0.3,
    ease: 'steps(3)',
  });

  // Quick distortion of the overlay
  await gsap.to(overlay, {
    skewX: -5,
    scaleY: 1.02,
    duration: config.exitDuration * 0.2,
    ease: 'power2.inOut',
  });

  // Render new page behind overlay
  await renderNewPage();

  // Reset overlay
  gsap.set(overlay, { skewX: 0, scaleY: 1 });

  // Glitch out the overlay
  await gsap.to(overlay, {
    opacity: 0,
    duration: config.enterDuration,
    ease: 'steps(5)',
  });

  removeOverlay(overlay);

  // Apply entrance glitch to new content
  glitch(app, 3);
}

/**
 * Execute the scanline transition (CRT feel)
 */
async function scanlineTransition(
  _app: HTMLElement,
  config: TransitionConfig,
  renderNewPage: () => Promise<void>
): Promise<void> {
  const overlay = createOverlay('scanline');

  // Scanline sweep down
  gsap.set(overlay, {
    scaleY: 0,
    transformOrigin: 'top center',
  });

  await gsap.to(overlay, {
    scaleY: 1,
    duration: config.exitDuration,
    ease: 'power1.in',
  });

  // Render new page
  await renderNewPage();

  // Scanline sweep up (reveal)
  gsap.set(overlay, {
    transformOrigin: 'bottom center',
  });

  await new Promise(resolve => setTimeout(resolve, config.staggerDelay * 1000));

  await gsap.to(overlay, {
    scaleY: 0,
    duration: config.enterDuration,
    ease: 'power1.out',
  });

  removeOverlay(overlay);
}

/**
 * Execute the terminal transition (text clear effect)
 */
async function terminalTransition(
  _app: HTMLElement,
  config: TransitionConfig,
  renderNewPage: () => Promise<void>
): Promise<void> {
  const overlay = createOverlay('terminal');

  // Terminal lines appear
  const lines: HTMLElement[] = [];
  const lineCount = 8;

  for (let i = 0; i < lineCount; i++) {
    const line = document.createElement('div');
    line.className = 'terminal-clear-line';
    line.style.top = `${(i / lineCount) * 100}%`;
    line.style.animationDelay = `${i * 0.03}s`;
    overlay.appendChild(line);
    lines.push(line);
  }

  // Show overlay with lines
  gsap.set(overlay, { opacity: 1 });

  // Animate lines growing
  await gsap.to(lines, {
    scaleX: 1,
    duration: config.exitDuration,
    stagger: 0.03,
    ease: 'power2.in',
  });

  // Render new page
  await renderNewPage();

  await new Promise(resolve => setTimeout(resolve, config.staggerDelay * 1000));

  // Lines shrink away
  await gsap.to(lines, {
    scaleX: 0,
    duration: config.enterDuration,
    stagger: 0.02,
    ease: 'power2.out',
  });

  removeOverlay(overlay);
}

/**
 * Execute the pixelate transition
 */
async function pixelateTransition(
  _app: HTMLElement,
  config: TransitionConfig,
  renderNewPage: () => Promise<void>
): Promise<void> {
  const overlay = createOverlay('pixelate');

  // Create pixel grid
  const gridSize = 12;
  const pixels: HTMLElement[] = [];

  for (let i = 0; i < gridSize * gridSize; i++) {
    const pixel = document.createElement('div');
    pixel.className = 'pixel-cell';
    pixel.style.width = `${100 / gridSize}%`;
    pixel.style.height = `${100 / gridSize}%`;
    overlay.appendChild(pixel);
    pixels.push(pixel);
  }

  // Shuffle for random reveal
  const shuffledPixels = [...pixels].sort(() => Math.random() - 0.5);

  // Pixels appear randomly
  gsap.set(overlay, { opacity: 1 });
  await gsap.to(shuffledPixels, {
    opacity: 1,
    duration: config.exitDuration,
    stagger: {
      each: config.exitDuration / (gridSize * gridSize * 0.7),
      from: 'random',
    },
    ease: 'none',
  });

  // Render new page
  await renderNewPage();

  await new Promise(resolve => setTimeout(resolve, config.staggerDelay * 1000));

  // Pixels disappear randomly
  const reshuffled = [...pixels].sort(() => Math.random() - 0.5);
  await gsap.to(reshuffled, {
    opacity: 0,
    duration: config.enterDuration,
    stagger: {
      each: config.enterDuration / (gridSize * gridSize * 0.7),
      from: 'random',
    },
    ease: 'none',
  });

  removeOverlay(overlay);
}

/**
 * Execute the static transition (TV static interruption)
 */
async function staticTransition(
  _app: HTMLElement,
  config: TransitionConfig,
  renderNewPage: () => Promise<void>
): Promise<void> {
  const overlay = createOverlay('static');

  // Start with black
  gsap.set(overlay, { opacity: 0 });

  // Flicker in with static
  overlay.classList.add('static-active');

  await gsap.to(overlay, {
    opacity: 1,
    duration: config.exitDuration * 0.6,
    ease: 'steps(6)',
  });

  // Brief full static
  await new Promise(resolve => setTimeout(resolve, 150));

  // Render new page
  await renderNewPage();

  await new Promise(resolve => setTimeout(resolve, config.staggerDelay * 1000));

  // Flicker out
  await gsap.to(overlay, {
    opacity: 0,
    duration: config.enterDuration,
    ease: 'steps(4)',
  });

  overlay.classList.remove('static-active');
  removeOverlay(overlay);
}

/**
 * Execute the crack transition (screen shatter effect)
 */
async function crackTransition(
  _app: HTMLElement,
  config: TransitionConfig,
  renderNewPage: () => Promise<void>
): Promise<void> {
  return new Promise<void>((resolve) => {
    screenCrack(
      async () => {
        // Render new page after crack animation completes
        await renderNewPage();

        // Brief entrance animation for the new content
        const app = document.getElementById('app');
        if (app) {
          gsap.from(app, {
            opacity: 0,
            duration: config.enterDuration,
            ease: 'power2.out',
            onComplete: resolve,
          });
        } else {
          resolve();
        }
      },
      {
        originX: 50,
        originY: 50,
        crackCount: 14,
        fragmentCount: 36,
        crackColor: 'var(--red)',
      }
    );
  });
}

/**
 * Thematic phrases for the typewriter transition
 * Inspired by: Terminator 2, Pet Sematary 2, Lord of Illusions, Syphon Filter, John from Cincinnati
 */
const TYPEWRITER_PHRASES = [
  // Machine/fate themes
  'THE FUTURE IS NOT SET',
  'JUDGMENT PROTOCOL INITIATED',
  'NEURAL NET PROCESSOR ONLINE',
  'SKYNET DEFENSE NETWORK',
  'NO FATE BUT WHAT WE MAKE',
  'TERMINATE CONNECTION',
  // Death/resurrection themes
  'SOMETIMES DEAD IS BETTER',
  'THE GROUND IS SOUR HERE',
  'WHAT YOU BURY COMES BACK',
  'THE BARRIER WAS NOT MEANT TO BE CROSSED',
  'IT WANTS TO COME BACK',
  // Illusion/magic themes
  'THE FLESH IS A TRAP',
  'I WAS BORN TO MURDER THE WORLD',
  'FIRST COMES DECEPTION',
  'NIX IS COMING',
  'SWEAR TO ME THAT YOU ARE AFRAID',
  // Covert ops/virus themes
  'SYPHON FILTER ACTIVATED',
  'PHARCOM VIRAL CONTAINMENT BREACH',
  'AGENCY PROTOCOL SEVEN',
  'ELIMINATE ALL WITNESSES',
  'THE CONSPIRACY RUNS DEEPER',
  // Mysterious stranger themes
  'THE STRANGER WALKS AMONG US',
  'MISTER THINKS YOU SHOULD PADDLE OUT',
  'SOME THINGS CANNOT BE EXPLAINED',
  'CINCINNATI CALLING',
  'THE WAVE IS COMING',
  // Corruption/glitch
  '> SIGNAL LOST',
  '> VECTOR CORRUPTION',
  '> MEMORY FAULT',
  '> IDENTITY UNKNOWN',
  '> CARRIER LOST',
];

/**
 * Execute the typewriter cascade transition
 * Screen fills with thematic text line by line, then clears to reveal new page
 */
async function typewriterTransition(
  _app: HTMLElement,
  config: TransitionConfig,
  renderNewPage: () => Promise<void>
): Promise<void> {
  const overlay = createOverlay('typewriter');
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100vw';
  overlay.style.height = '100vh';
  overlay.style.zIndex = '10000';
  overlay.style.overflow = 'hidden';
  overlay.style.opacity = '1';
  overlay.style.background = '#0a0a0a';

  // Calculate grid dimensions based on monospace character sizing
  const charWidth = 10;
  const charHeight = 20;
  const cols = Math.ceil(window.innerWidth / charWidth);
  const rows = Math.ceil(window.innerHeight / charHeight);

  // Glitch characters for padding
  const glitchChars = '█▓▒░▌▐│┃┆┇┊┋';

  // Create the text container
  const textContainer = document.createElement('div');
  textContainer.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    font-family: 'IBM Plex Mono', 'Space Mono', monospace;
    font-size: 14px;
    line-height: ${charHeight}px;
    color: #8b0000;
    background: #0a0a0a;
    overflow: hidden;
    white-space: pre;
    opacity: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  `;

  // Create rows - mix of phrases and glitch lines
  const rowElements: HTMLElement[] = [];
  let phraseIndex = Math.floor(Math.random() * TYPEWRITER_PHRASES.length);

  for (let r = 0; r < rows; r++) {
    const row = document.createElement('div');
    row.style.cssText = `
      height: ${charHeight}px;
      width: 100vw;
      overflow: hidden;
      opacity: 0;
      text-align: center;
    `;

    let rowText = '';

    // Every few rows, insert a phrase
    if (r % 3 === 1 && Math.random() > 0.3) {
      const phrase = TYPEWRITER_PHRASES[phraseIndex % TYPEWRITER_PHRASES.length];
      phraseIndex++;

      // Center the phrase with glitch padding
      const padding = Math.floor((cols - phrase.length) / 2);
      for (let i = 0; i < padding; i++) {
        rowText += glitchChars[Math.floor(Math.random() * glitchChars.length)];
      }
      rowText += phrase;
      for (let i = rowText.length; i < cols; i++) {
        rowText += glitchChars[Math.floor(Math.random() * glitchChars.length)];
      }
    } else {
      // Glitch line
      for (let c = 0; c < cols; c++) {
        rowText += glitchChars[Math.floor(Math.random() * glitchChars.length)];
      }
    }

    row.textContent = rowText;
    textContainer.appendChild(row);
    rowElements.push(row);
  }

  overlay.appendChild(textContainer);

  // Phase 1: Cascade rows in from top to bottom
  await gsap.to(rowElements, {
    opacity: 1,
    duration: 0.015,
    stagger: config.exitDuration / rows,
    ease: 'none',
  });

  // Brief hold
  await new Promise(resolve => setTimeout(resolve, config.staggerDelay * 1000));

  // Render new page behind overlay
  await renderNewPage();

  // Phase 2: Cascade rows out from top to bottom (reveal new page)
  await gsap.to(rowElements, {
    opacity: 0,
    duration: 0.015,
    stagger: config.enterDuration / rows,
    ease: 'none',
  });

  removeOverlay(overlay);
}

/**
 * Execute a page transition
 */
async function executeTransition(
  config: TransitionConfig,
  renderNewPage: () => Promise<void>
): Promise<void> {
  const app = document.getElementById('app');
  if (!app) {
    await renderNewPage();
    return;
  }

  // Skip animations entirely for reduced motion
  if (prefersReducedMotion()) {
    await gsap.to(app, { opacity: 0, duration: 0.1 });
    await renderNewPage();
    gsap.set(app, { opacity: 1 });
    return;
  }

  switch (config.type) {
    case 'fade':
      await fadeTransition(app, config, renderNewPage);
      break;
    case 'glitch':
      await glitchTransition(app, config, renderNewPage);
      break;
    case 'scanline':
      await scanlineTransition(app, config, renderNewPage);
      break;
    case 'terminal':
      await terminalTransition(app, config, renderNewPage);
      break;
    case 'pixelate':
      await pixelateTransition(app, config, renderNewPage);
      break;
    case 'static':
      await staticTransition(app, config, renderNewPage);
      break;
    case 'crack':
      await crackTransition(app, config, renderNewPage);
      break;
    case 'typewriter':
      await typewriterTransition(app, config, renderNewPage);
      break;
    default:
      await fadeTransition(app, config, renderNewPage);
  }
}

// Track if a transition is in progress
let isTransitioning = false;
let pendingTransition: { from: string; to: string } | null = null;

/**
 * Page transition manager - integrates with the router
 */
class TransitionManager {
  private enabled = true;

  /**
   * Enable or disable transitions
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if transitions are enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Perform a page transition
   */
  async transition(
    from: string,
    to: string,
    renderNewPage: () => Promise<void>
  ): Promise<void> {
    if (!this.enabled || isTransitioning) {
      await renderNewPage();
      return;
    }

    isTransitioning = true;
    pendingTransition = { from, to };

    try {
      const config = getTransitionConfig(from, to);
      await executeTransition(config, renderNewPage);
    } finally {
      isTransitioning = false;
      pendingTransition = null;
    }
  }

  /**
   * Check if a transition is in progress
   */
  isInProgress(): boolean {
    return isTransitioning;
  }

  /**
   * Get the pending transition info
   */
  getPendingTransition(): { from: string; to: string } | null {
    return pendingTransition;
  }
}

// Singleton instance
export const transitionManager = new TransitionManager();

/**
 * Initialize the transition system with the router
 * This must be called after routes are registered
 */
export function initializeTransitions(): void {
  // We intercept navigation by modifying how the router renders
  // This is done by wrapping the page render functions
  // The actual integration happens in main.ts
}

/**
 * Wrapper function to create a transitioning render function
 * Use this when registering routes to enable transitions
 */
export function withTransition(
  render: () => void | Promise<void>,
  pageName: string
): () => Promise<void> {
  return async () => {
    // Get the previous path from router
    const from = router.getCurrentPath();
    const to = pageName;

    // If this is the initial load (from === to === ''), skip transition
    if (from === '' && to === '') {
      await render();
      return;
    }

    // Perform transition
    await transitionManager.transition(from, to, async () => {
      await render();
    });
  };
}

/**
 * After navigate callback that can be used to trigger entrance animations
 */
export function createEntranceAnimationCallback(): RouteChangeListener {
  return (event: NavigationEvent) => {
    if (event.prevented) return;

    const app = document.getElementById('app');
    if (!app) return;

    // Add entrance class for CSS animations
    const pageContainer = app.querySelector('.page, [class*="-container"]') as HTMLElement;
    if (pageContainer) {
      pageContainer.classList.add('page--enter');

      // Remove after animation completes
      setTimeout(() => {
        pageContainer.classList.remove('page--enter');
      }, 500);
    }
  };
}
